/**
 * <TutorAbsenceMessageStep> — le message aux familles et les consignes du remplaçant (organism).
 *
 * La dernière étape d'une absence dans l'ancien `MoveSessionReasonComponent` : l'encadré « Ton
 * absence impacte directement les élèves… », le message d'excuse aux parents et aux élèves (50
 * caractères au moins, compteur dessous), les consignes pour le remplaçant (l'exemple nomme le
 * premier élève), et la case qui certifie le message. Ce qui permet d'envoyer :
 * `canSendAbsenceMessage`, que le gabarit lit pour son bouton.
 */
import React from "react"

import { Checkbox } from "../../atoms/Checkbox"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"

/** Le minimum du message d'excuse aux familles (`isParentMessageValid`). */
const EXCUSE_MIN_LENGTH = 50

/** Le message est assez long, les consignes écrites, la case cochée : l'ancien `cantContinue`, retourné. */
export function canSendAbsenceMessage(input: { message: string; instructions: string; excuseConfirmed: boolean }) {
    return input.message.length >= EXCUSE_MIN_LENGTH && input.instructions.trim() !== "" && input.excuseConfirmed
}

export type TutorAbsenceMessageStepProps = {
    message: string
    onChangeMessage: (text: string) => void
    instructions: string
    onChangeInstructions: (text: string) => void
    excuseConfirmed: boolean
    onToggleExcuseConfirmed: (next: boolean) => void
    /** « Jules » — le premier élève, dans l'exemple des consignes. */
    firstStudentName: string
    testID?: string
}

export function TutorAbsenceMessageStep(props: TutorAbsenceMessageStepProps) {
    const {
        message,
        onChangeMessage,
        instructions,
        onChangeInstructions,
        excuseConfirmed,
        onToggleExcuseConfirmed,
        firstStudentName,
        testID,
    } = props
    const messageOk = message.length >= EXCUSE_MIN_LENGTH

    return (
        <Stack gap="4" testID={testID}>
            <AlertCard
                severity="warning"
                title="Important"
                message="Ton absence impacte directement les élèves, qui devront s'adapter au masteur remplaçant. Explique clairement la raison de ton absence aux parents et aux élèves pour maintenir leur confiance."
            />
            <Stack gap="1">
                <TextInput
                    label="Écris un message aux parents/élèves*"
                    value={message}
                    onChangeText={onChangeMessage}
                    placeholder="Bonjour, je vous présente mes excuses mais je dois m'absenter pour cette séance en raison de..."
                    multiline
                    numberOfLines={4}
                />
                <Text size="text-xs" color={messageOk ? "text-success" : "text-muted"}>
                    {`${message.length}/${EXCUSE_MIN_LENGTH} caractères minimum requis`}
                </Text>
            </Stack>
            <TextInput
                label="Consignes pour le remplaçant*"
                value={instructions}
                onChangeText={onChangeInstructions}
                placeholder={`On a travaillé dernièrement avec ${firstStudentName} sur... Bien faire attention à ...`}
                multiline
                numberOfLines={5}
            />
            <Checkbox
                checked={excuseConfirmed}
                onChange={onToggleExcuseConfirmed}
                label="Je confirme avoir rédigé un message d'excuse approprié pour les parents/élèves"
            />
        </Stack>
    )
}
